// Chave de idempotência do pedido EM ABERTO, guardada na aba (sessionStorage) — QA 31/10/2026.
//
// Antes ela vivia num useRef do modal: se a 1ª confirmação caía na rede DEPOIS de o backend registrar o pedido e
// o cliente fechava e reabria o checkout, o modal remontava com uma chave NOVA e o reenvio criava um pedido
// DUPLICADO (estoque e impressão 2×). Agora a chave sobrevive ao fechar/reabrir enquanto a sacola for a mesma;
// sacola diferente = pedido diferente = chave nova. Sucesso libera a chave.
import type { BagItem } from './bag';
import { newIdempotencyKey } from './order';

const PREFIXO = 'vitto:chave-pedido:';
// Sem sessionStorage (aba privada/bloqueada): a chave vale enquanto a página estiver aberta.
const emMemoria = new Map<string, { chave: string; assinatura: string }>();

/** Assinatura da sacola: o que muda o pedido (linhas, quantidades e observações). */
export function assinaturaDaSacola(bag: readonly Pick<BagItem, 'key' | 'qty' | 'observacao'>[]): string {
  return bag.map((i) => `${i.key}×${i.qty}×${(i.observacao ?? '').trim()}`).join('|');
}

function armazenamento(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * A chave do pedido em aberto deste escopo (loja ou mesa) para esta sacola: reusa a guardada quando a sacola é a
 * mesma; senão gera e guarda uma nova.
 */
export function chaveDoPedido(escopo: string, assinatura: string): string {
  const memoria = emMemoria.get(escopo);
  if (memoria && memoria.assinatura === assinatura) return memoria.chave;
  const st = armazenamento();
  if (st) {
    try {
      const salvo = JSON.parse(st.getItem(PREFIXO + escopo) ?? 'null') as { chave?: unknown; assinatura?: unknown } | null;
      if (salvo && typeof salvo.chave === 'string' && salvo.chave && salvo.assinatura === assinatura) return salvo.chave;
    } catch {
      /* valor corrompido: segue com uma chave nova */
    }
  }
  const chave = newIdempotencyKey();
  emMemoria.set(escopo, { chave, assinatura });
  try {
    st?.setItem(PREFIXO + escopo, JSON.stringify({ chave, assinatura }));
  } catch {
    /* armazenamento cheio/bloqueado: a chave vale só em memória, como antes */
  }
  return chave;
}

/** Libera a chave do escopo (pedido confirmado, ou o conteúdo mudou de propósito). */
export function liberarChaveDoPedido(escopo: string): void {
  emMemoria.delete(escopo);
  try {
    armazenamento()?.removeItem(PREFIXO + escopo);
  } catch {
    /* sem armazenamento: nada guardado */
  }
}
